import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, Center, createStyles, ActionIcon, Group, Tooltip, Menu, Loader, Select, useMantineTheme, Badge } from "@mantine/core";
import { useClipboard, useMediaQuery } from "@mantine/hooks";
import SurveyForm from "../../components/survey-form/survey-form.component";
import SurveyPlot from "../../components/survey-plot/survey-plot";
import SurveyHistory from "../../components/survey-history/survey-history.component";
import { IconCopy, IconInfoCircle, IconX, IconTable, IconCodeDots, IconDownload, IconLink, IconHistory, IconHistoryOff, IconCloudUpload, IconChartBar, } from "@tabler/icons-react";
import { fetchUserSurveys } from "../../utils/firebase/firebase.utils";
import { useUserAuth } from "../../contexts/user.context";
import { downloadCSV, downloadJSON, getCopyLink, examples, isEmpty } from "../../assets/constants";
import { useJuneProvider } from "../../contexts/analytics.context";
import { showNotification } from '@mantine/notifications';
import { useGlobalSurveysProvider } from "../../contexts/global-surveys.context";
import ConfidenceForm from "../../components/confidence-form/confidence-form.component";
import tsnejs from 'tsne';
import useUpdateSurveyData from "../../assets/custom-hooks/updateSurveyData";
import FileUpload from "../../components/file-upload/file-upload.component";
import html2canvas from 'html2canvas';
import SocialCard from "../../components/social-card/social-card";

const useStyles = createStyles((theme) => ({

    wrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },

    playgroundToolbar: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        height: '60px',
        padding: '0px 25px',
        borderBottom: '1px solid #f1f1f1',
    },

    hideForMobile: {
        [theme.fn.smallerThan('sm')]: {
            display: 'none'
        },
    },

    innerPlaygroundToolbarDiv: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        [theme.fn.smallerThan('sm')]: {
            width: '100%',
        },
    },

    toolbarIconWrapper: {
        [theme.fn.smallerThan('sm')]: {
            width: '100%',
        },
    },

    toolbarIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.dark[7],
    },

    canvas: {
        zIndex: '99999',
        top: '0px',
        position: 'absolute',
        width: '400px',
        height: '400px',
        border: '1px solid black',
    },

    loaderDiv: {
        zIndex: '99999',
        position: 'absolute',
        top: '60px',
        width: '100%',
        height: 'calc(100vh - 60px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
    },

    toolbarItemDisabled: {
        stroke: theme.colors.gray[5],
    },

    examplesSelect: {
        width: '252px',
        [theme.fn.smallerThan('sm')]: {
            width: '265px',
            flexGrow: '1',
        },

        '.mantine-Select-separatorLabel': {
            color: theme.colors.gray[6],
            textTransform: 'uppercase',
            fontWeight: '600',
        },

        '.mantine-Input-rightSection': {
            marginRight: '5px',
        }
    },

    betaText: {
        background: '#f5f5f5',
        padding: '6px',
        borderRadius: '12px',
    },

    modal: {
        '.mantine-Modal-title': {
            fontSize: '18px',
            fontWeight: '500',
        }
    },

}));

const numTSNESteps = 300;
const numTSNEStepsBeforePlot = 180;
const nClosest = 100;

const getConfidenceBadgeText = (confidence, isMedium) => {
    if (confidence === '') {
        return isMedium ? 'Estimating' : 'Estimating confidence';
    }
    return isMedium ? confidence : `Confidence: ${confidence}`;
}

const initialIconLoadingState = {
    copyLink: false,
}

// Adjust examples based on whether we are in production or development
const url = window.location.hostname;
const urlExamples = examples.filter((example) => example.url === url);
const sandboxURL = url === 'localhost' ? 'localhost:8888/simulator' : `https://${url}/simulator`;

// TSNE setup
const opt = { epsilon: 10, perplexity: 10 }; // epsilon is learning rate (10 = default)
const tsne = new tsnejs.tSNE(opt); // create a tSNE instance

const confidenceData = {
    'high': {
        color: 'green',
        text: 'Confidence is high as the target question is very similar to the questions in the training data.'
    },
    'medium': {
        color: 'orange',
        text: 'Confidence is medium as the target question is somewhat similar to the questions in the training data.'
    },
    'low': {
        color: 'red',
        text: 'Confidence is low as the target question is very different from the questions in the training data.'
    },
}

const getConfidence = (similarityScores, surveyDescriptions, customQuestionsAndResponses) => {
    // Set confidence to low if there is any conditioning
    if (surveyDescriptions.length > 0 || customQuestionsAndResponses.length > 0) return 'low'

    // Get 10 most similar and convert to numbers
    const topSimilarityScores = similarityScores.slice(0, 10).map((score) => Number(score));

    if (!topSimilarityScores.length) {
        throw new Error('The list should not be empty');
    }

    // let all_known = true;
    // surveyData.forEach((data) => {
    //     if (data.known === false) {
    //         all_known = false;
    //     }
    // });

    // if (all_known === true) return 'high'

    const sum = topSimilarityScores.map(parseFloat).reduce((a, b) => a + b, 0);
    const mean = sum / topSimilarityScores.length;

    if (mean < 0.82) return 'low';
    if (mean < 0.86) return 'medium';
    return 'high';
}

const checkIfSurveyHasEmbeddings = (surveyResponse) => {
    if (surveyResponse.closestTexts === undefined || surveyResponse.closestTexts.length === 0) return false;
    if (surveyResponse.embeddings2dJSON === undefined) return false;
    if (surveyResponse.similarityScores === undefined || surveyResponse.similarityScores.length === 0) return false;
    if (surveyResponse.similarQuestionDataJSON === undefined) return false;
    return true;
}

const Sandbox = () => {

    const { surveyId } = useParams();

    const theme = useMantineTheme();
    const isSmall = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);
    const isMedium = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`);
    const hasMouse = useMediaQuery(`(pointer:fine)`);

    const {
        exampleSurveys,
        setExampleSurveys,
        userSurveyHistory,
        setUserSurveyHistory,
        userSurveyHistoryLoaded,
        setUserSurveyHistoryLoaded
    } = useGlobalSurveysProvider();

    const updateSurveyData = useUpdateSurveyData();

    const [surveyResponse, setSurveyResponse] = useState({});
    const [surveyResponseId, setSurveyResponseId] = useState('');
    const [appLoading, setAppLoading] = useState(true);
    const [historyShown, setHistoryShown] = useState(false);
    const [exampleSearchValue, setExampleSearchValue] = useState('');
    const [historyLoadingText, setHistoryLoadingText] = useState('Loading...');
    const [currentUserId, setCurrentUserId] = useState('anon');
    const [iconLoadingState, setIconLoadingState] = useState(initialIconLoadingState);
    const [isRunningNewSurvey, setIsRunningNewSurvey] = useState(false);
    const [numCreditsAvailable, setNumCreditsAvailable] = useState(0);
    const [preparingData, setPreparingData] = useState(false);

    // File upload stuff
    const [fileModalOpened, setFileModalOpened] = useState(false);

    // Embeddings stuff
    const [embeddingsShown, setEmbeddingsShown] = useState(false);
    const [embeddings2d, setEmbeddings2d] = useState([]);
    const [step, setStep] = useState(-1);
    const [fitTSNE, setFitTSNE] = useState(false);
    const [embeddingText, setEmbeddingText] = useState([]);
    const [embeddingsState, setEmbeddingsState] = useState(0);
    const [selectedEmbeddingIndex, setSelectedEmbeddingIndex] = useState(-1);
    const [shownQuestionIndices, setShownQuestionIndices] = useState([]);
    const [embeddingScores, setEmbeddingScores] = useState([]);
    const [embeddingsError, setEmbeddingsError] = useState(false);
    const [similarQuestionData, setSimilarQuestionData] = useState([]);
    const [similarDataPlotPrimary, setSimilarDataPlotPrimary] = useState(false);

    // Question rewording stuff
    const [rewordingModalOpened, setRewordingModalOpened] = useState(false);

    // Confidence
    const [confidence, setConfidence] = useState('');
    // const confidence = 'low'
    const [estimatingConfidence, setEstimatingConfidence] = useState(false);

    // For stack tracking with fetches
    const isEmbeddingsRunningRef = useRef(false);
    const plotRef = useRef(null);
    const socialCardRef6001230 = useRef(null);
    // const socialCardRef300300 = useRef(null);

    const downloadPNG = async () => {
        try {
            const canvas = await html2canvas(plotRef.current);
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'results.png';
                a.click();
                URL.revokeObjectURL(url);
            }, 'results/png');
        } catch (error) {
            console.error('Error converting SVG to PNG', error);
        }
    };

    const copyPNG = async () => {
        try {
            const canvas = await html2canvas(plotRef.current);
            canvas.toBlob(async (blob) => {
                try {
                    const item = new window.ClipboardItem({ "image/png": blob });
                    await navigator.clipboard.write([item]);
                    console.log('Image copied to clipboard');
                } catch (error) {
                    console.error('Error copying image to clipboard', error);
                }
                showNotification({
                    title: 'Image copied',
                    color: "teal",
                    style: { top: '60px' }
                });
            });
        } catch (error) {
            console.error('Error converting to canvas', error);
            showNotification({
                title: 'Error copying image',
                color: "red",
                style: { top: '60px' }
            });
        }
    };

    const getSocialCardsBase64 = async () => {
        try {
            // const canvas300 = await html2canvas(socialCardRef300300.current);
            const canvas600 = await html2canvas(socialCardRef6001230.current);
            // const base64_300 = canvas300.toDataURL('image/png');
            const base64_600 = canvas600.toDataURL('image/png');
            console.log('base64_600', base64_600);
            return {  base64_600 };
        } catch (error) {
            console.error('Error converting Canvas to Base64', error);
            throw error;
        }
    };


    const analytics = useJuneProvider();
    const clipboard = useClipboard({ timeout: 1500 });
    const {
        currentUser,
        setNumFreeCreditsUsed,
        numFreeCredits,
        numFreeCreditsUsed,
        setUserError,
        customModels,
    } = useUserAuth();

    const fetchAuthUserSurveys = async () => {
        if (!currentUser) return [];
        try {
            let userSurveys = await fetchUserSurveys(currentUser);
            if (userSurveys === undefined) userSurveys = [];
            setUserSurveyHistory(userSurveys);
            if (userSurveys.length === 0) setHistoryLoadingText('No surveys found');
        } catch (error) {
            console.log('error', error);
            setUserError(true);
        }
    }

    // Load the example surveys
    const loadExamples = async () => {
        if (exampleSurveys.length !== 0) return;
        // Get value field from every example
        const surveyIds = urlExamples.map((example) => example.value);
        const loadedExamplesTemp = await fetch("/.netlify/functions/getSurveysWithLink", { method: "POST", body: JSON.stringify({ surveyIds }) });
        const { loadedSurveyData } = await loadedExamplesTemp.json();
        setExampleSurveys(loadedSurveyData);
    }

    const { classes, cx } = useStyles();

    useEffect(() => {
        const loadSurveys = async () => {
            if (userSurveyHistoryLoaded === false && isSmall === false) {
                if (exampleSurveys.length === 0) await loadExamples();
                await fetchAuthUserSurveys();
                setUserSurveyHistoryLoaded(true);
            }
        }
        loadSurveys();
    }, [isSmall]);

    useEffect(() => {
        isEmbeddingsRunningRef.current = false;
        setSelectedEmbeddingIndex(-1);
        setIsRunningNewSurvey(false);
        setConfidence('');
        setEmbeddingsState(0);
        setEmbeddingsShown(false);
        setEmbeddings2d([]);
        setSimilarQuestionData([]);
        setEmbeddingText([]);
        setEmbeddingScores([]);
        setEstimatingConfidence(false);
        setFitTSNE(false);
        setSimilarDataPlotPrimary(false);
        if (isEmpty(surveyResponse)) {
            setExampleSearchValue('');
        } else {
            const alreadyHasEmbeddings = checkIfSurveyHasEmbeddings(surveyResponse);
            if (alreadyHasEmbeddings) {
                const { similarityScores, surveyDescriptions, customQuestionsAndResponses } = surveyResponse
                const newConfidence = getConfidence(similarityScores, surveyDescriptions, customQuestionsAndResponses);
                setConfidence(newConfidence);
            }
            // Check if survey is in example surveys using some
            const surveyInExamples = exampleSurveys.some((example) => example.id === surveyResponse.id);
            if (surveyInExamples) {
                setExampleSearchValue(surveyResponse.id);
                if (analytics) {
                    analytics.track({
                        event: 'Loaded sandbox example',
                        userId: currentUserId,
                        properties: {
                            surveyId: surveyResponse.id,
                        }
                    });
                }
            } else {
                setExampleSearchValue('');
            }
        }
    }, [surveyResponseId]);

    useEffect(() => {
        const newSurveyId = isEmpty(surveyResponse) ? '' : surveyResponse.id;
        // console.log('newSurveyId',newSurveyId)
        setSurveyResponseId(newSurveyId);
    }, [surveyResponse]);

    useEffect(() => {
        const loadInitialState = async () => {
            if (surveyId !== undefined) {
                const dataDict = await fetch("/.netlify/functions/getSurveysWithLink", { method: "POST", body: JSON.stringify({ surveyIds: [surveyId] }) });
                const { loadedSurveyData } = await dataDict.json();
                const loadedSurveyDataFirstElement = loadedSurveyData[0];
                // console.log('loadedSurveyDataFirstElement', loadedSurveyDataFirstElement.surveyType);
                if (loadedSurveyDataFirstElement !== null && loadedSurveyDataFirstElement !== undefined) {
                    setSurveyResponse(loadedSurveyDataFirstElement);
                }
            }
            setAppLoading(false);
            if (!isSmall && exampleSurveys.length === 0) {
                await loadExamples();
            }
            if (currentUser) setCurrentUserId(currentUser.uid);
        }
        loadInitialState();
    }, []);

    useEffect(() => {
        const runTSNE = async () => {
            tsne.step();
            const Y = tsne.getSolution();
            if (step < numTSNESteps) {
                setEmbeddings2d(Y);
                setStep(step + 1);
                if (embeddingsState !== 4 && step >= numTSNEStepsBeforePlot) {
                    setEmbeddingsState(4);
                }
            } else {
                setFitTSNE(false);
                setStep(0);
                setIconLoadingState({ ...iconLoadingState, embeddings: false });
                // if (!isEmbeddingsRunningRef.current) return;
                const newSurveyData = {
                    ...surveyResponse,
                    embeddings2dJSON: JSON.stringify(Y),
                    closestTexts: embeddingText,
                    similarityScores: embeddingScores,
                    similarQuestionDataJSON: JSON.stringify(similarQuestionData),
                };
                updateSurveyData(newSurveyData);
                setSurveyResponse(newSurveyData);
            }
        }
        if (fitTSNE) runTSNE();
    }, [step, fitTSNE]);

    const toggleEmbeddings = async () => {
        if (embeddingsShown) {
            // setFitTSNE(false);
            // setStep(0);
            setEmbeddingsShown(false);
            isEmbeddingsRunningRef.current = false;
            setSelectedEmbeddingIndex(-1);
            setSimilarDataPlotPrimary(false);
        } else {
            setStep(0);
            retrieveDataAndPlotEmbeddings();
        }
    }

    useEffect(() => {
        if (embeddingScores.length > 0 && surveyResponse.id !== undefined) {
            const { surveyDescriptions, customQuestionsAndResponses } = surveyResponse
            const newConfidence = getConfidence(embeddingScores, surveyDescriptions, customQuestionsAndResponses);
            setConfidence(newConfidence);
        }
    }, [embeddingScores]);

    const retrieveDataAndPlotEmbeddings = async () => {
        // If data already exists, do that
        const alreadyHasEmbeddings = checkIfSurveyHasEmbeddings(surveyResponse);
        if (alreadyHasEmbeddings) {
            const parsed2dJson = JSON.parse(surveyResponse.embeddings2dJSON);
            const parsedSimilarQuestionData = JSON.parse(surveyResponse.similarQuestionDataJSON);
            setSimilarQuestionData(parsedSimilarQuestionData);
            setEmbeddings2d(parsed2dJson);
            setEmbeddingText(surveyResponse.closestTexts);
            setEmbeddingScores(surveyResponse.similarityScores);
            setEmbeddingsShown(true);
            setEmbeddingsState(4);
        } else {
            try {
                if (analytics) {
                    analytics.track({
                        event: 'Started embeddings investigation',
                        userId: currentUserId,
                        properties: {
                            surveyId: surveyResponse.id,
                        }
                    });
                }
                isEmbeddingsRunningRef.current = true;
                setEstimatingConfidence(true);
                setEmbeddingsState(0);
                setEmbeddingsError(false);
                // Otherwise, fetch embeddings
                setIconLoadingState({ ...iconLoadingState, embeddings: true });
                setEmbeddingsShown(true);
                const rawTargetQuestionEmbedding = await fetch("/.netlify/functions/getEmbedding",
                    {
                        method: "POST", body: JSON.stringify({
                            surveyQuestion: surveyResponse.surveyQuestion,
                            model: surveyResponse.surveyModel,
                            product: surveyResponse.product,
                            questionType: surveyResponse.questionType,
                            country: surveyResponse.surveyCountry,
                        })
                    });
                if (!isEmbeddingsRunningRef.current) return;
                setEmbeddingsState(1);
                const { targetEmbedding } = await rawTargetQuestionEmbedding.json();
                // Fetch getEmbeddings function
                const isDebug = window.location.hostname.includes('roundtable') === false;
                const embeddingsFetchResults = await fetch("/.netlify/functions/fetchClosestEmbeddings",
                    { method: "POST", body: JSON.stringify({ targetEmbedding, nClosest, isDebug, model: surveyResponse.surveyModel, }) });
                if (!isEmbeddingsRunningRef.current) return;
                const { closestEmbeddingsAndScores } = await embeddingsFetchResults.json();
                // Estimate and save confidence if it hasn't been estimated yet
                const closestTexts = closestEmbeddingsAndScores.map(e => ({
                    question: e.question,
                    options: e.options,
                    // country: e.country,
                    // product: e.product,
                    // questionType: e.questionType,
                }));
                // closestTexts.unshift(surveyResponse.surveyQuestion);
                setEmbeddingText(closestTexts);
                // similarityScores.unshift(1.0);
                const similarityScores = closestEmbeddingsAndScores.map(e => e.similarity);
                setEmbeddingScores(similarityScores);
                // Set shown indices to 0 through nClosest (0,1,...,nClosest)
                setShownQuestionIndices([...Array(nClosest + 1).keys()]);
                let closestEmbeddingsNoText = closestEmbeddingsAndScores.map((e) => e.embedding);
                const closestIndices = closestEmbeddingsAndScores.map(e => e.index);
                const rawDataForSimilarQuestions = await fetch("/.netlify/functions/fetchSimilarQuestionData",
                    { method: "POST", body: JSON.stringify({ closestIndices, isDebug, model: surveyResponse.surveyModel }) });
                const { dataForSimilarQuestions } = await rawDataForSimilarQuestions.json();
                if (!isEmbeddingsRunningRef.current) return;
                setSimilarQuestionData(dataForSimilarQuestions);
                setEmbeddingsState(2);
                tsne.initDataDist(closestEmbeddingsNoText);
                setFitTSNE(true);
                setStep(0);
                if (analytics) {
                    analytics.track({
                        event: 'Finished embeddings investigations',
                        userId: currentUserId,
                        properties: {
                            surveyId: surveyResponse.id,
                            surveyQuestion: surveyResponse.surveyQuestion,
                        }
                    });
                }
            } catch (error) {
                console.log('error', error)
                if (analytics) {
                    analytics.track({
                        event: 'Embeddings investigations error',
                        userId: currentUserId,
                        properties: {
                            surveyId: surveyResponse.id,
                            error: error,
                        }
                    });
                }
                setEmbeddings2d([]);
                setFitTSNE(false);
                setEmbeddingsError(true);
                setSimilarQuestionData([]);
                setIconLoadingState({ ...iconLoadingState, embeddings: false });
            }
            setEstimatingConfidence(false);
        }
    }

    useEffect(() => {
        setNumCreditsAvailable(Math.max(0, numFreeCredits - numFreeCreditsUsed))
    }, [numFreeCredits, numFreeCreditsUsed]);

    useEffect(() => {
        if (isSmall) setFileModalOpened(false);
    }, [isSmall]);


    const updateCreditUsage = (numSurveysInBatch) => {
        setNumFreeCreditsUsed(numFreeCreditsUsed + numSurveysInBatch);
    }

    const HistoryIcon = historyShown ? IconHistoryOff : IconHistory;

    const hideToolbarButtons = Object.keys(surveyResponse).length === 0 || isRunningNewSurvey;
    const isMCQ = Object.keys(surveyResponse).length > 0 && (surveyResponse.surveyType === undefined || surveyResponse.surveyType === 'MCQ');
    const showConfidence = (isRunningNewSurvey === false && isMCQ) && (confidence !== '' || estimatingConfidence);

    const surveysStillLoading = exampleSurveys.length === 0 || userSurveyHistoryLoaded === false;

    const showLoader = isSmall === false && userSurveyHistoryLoaded === false && exampleSurveys.length === 0;

    return (
        <>
            <Helmet>
                <title>Sandbox | Roundtable</title>
                <meta name='description' content='Quickly simulate conversations, surveys and interactions with your customers using Roundtable.' />
            </Helmet>
            <div className={classes.loaderDiv} style={{ display: appLoading ? 'flex' : 'none' }}>
                <Loader color="dark" style={{ display: showLoader ? 'block' : 'none' }} />
            </div>
            <div className={classes.wrapper}>
                <div className={classes.playgroundToolbar}>
                    <div className={cx(classes.innerPlaygroundToolbarDiv, classes.hideForMobile)}>
                        <FileUpload
                            currentUser={currentUser}
                            userSurveyHistory={userSurveyHistory}
                            setUserSurveyHistory={setUserSurveyHistory}
                            updateCreditUsage={updateCreditUsage}
                            baseURL={sandboxURL}
                            setFileModalOpened={setFileModalOpened}
                            fileModalOpened={fileModalOpened}
                        />
                        <Group
                            spacing='md'
                            position='apart'
                            className={classes.toolbarIconWrapper}
                            sx={{
                                transform: 'translateX(-5px)',
                            }}
                        >
                            <Tooltip
                                label={`${historyShown ? 'Hide' : 'Show'} history`}
                                color="dark"
                                className={classes.hideForMobile}
                                position='right'
                            >
                                <ActionIcon
                                    loading={surveysStillLoading}
                                    variant='transparent'
                                    size={32}
                                    disabled={currentUser === null}
                                    onMouseDown={() => {
                                        setHistoryShown(!historyShown);
                                    }}
                                >
                                    <HistoryIcon
                                        stroke={1.5}
                                        size={20}
                                        color='black'
                                        className={currentUser === null ? classes.toolbarItemDisabled : undefined}
                                    />

                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </div>
                    <div className={classes.innerPlaygroundToolbarDiv}>
                        <Group
                            spacing='md'
                            position='apart'
                            className={classes.toolbarIconWrapper}
                        >
                            {
                                showConfidence &&
                                <Tooltip
                                    multiline
                                    label={confidence == '' ? 'gray' : confidenceData[confidence]['text']}
                                    color={confidence == '' ? 'gray' : confidenceData[confidence]['color']}
                                    className={classes.hideForMobile}
                                    position='bottom'
                                    width={210}
                                    disabled={confidence === ''}
                                >
                                    <Badge
                                        color={confidence == '' ? 'gray' : confidenceData[confidence]['color']}
                                        radius='lg'
                                        leftSection={
                                            <Center>
                                                {confidence === '' ? <Loader size={12} /> : <IconInfoCircle size={12} />}
                                            </Center>
                                        }
                                        className={classes.hideForMobile}
                                    >
                                        {getConfidenceBadgeText(confidence, isMedium)}
                                    </Badge>
                                </Tooltip>
                            }
                            {
                                hideToolbarButtons === false &&
                                <>
                                    {
                                        isMCQ &&
                                        <>
                                            <Button
                                                size='xs'
                                                variant='outline'
                                                onClick={toggleEmbeddings}
                                                style={{ width: isMedium ? '115px' : '140px' }}
                                                className={classes.hideForMobile}
                                                radius='md'
                                            >
                                                {embeddingsShown ? 'View results' : isMedium ? 'Investigate' : 'Investigate results'}
                                            </Button>
                                            {/* {
                                                (surveyResponse.surveyModel.includes('GSS')) &&
                                                <Tooltip
                                                    label='Reword question'
                                                    color='dark'
                                                    className={classes.hideForMobile}
                                                >
                                                    <ActionIcon
                                                        variant='transparent'
                                                        sx={{
                                                            '&[data-disabled]': {
                                                                background: 'none',
                                                                border: 'none',
                                                            }
                                                        }}
                                                        size={32}
                                                        onClick={() => {
                                                            setRewordingModalOpened(true);
                                                        }}
                                                    >
                                                        <IconArrowsShuffle
                                                            stroke={1.5}
                                                            size={20}
                                                            color='black'
                                                        />
                                                    </ActionIcon>
                                                </Tooltip>
                                            } */}
                                        </>
                                    }
                                    <Tooltip label='Generate link' color='dark'>
                                        <ActionIcon
                                            variant='transparent'
                                            sx={{
                                                '&[data-disabled]': {
                                                    background: 'none',
                                                    border: 'none',
                                                }
                                            }}
                                            onClick={async () => {
                                                setIconLoadingState({ ...iconLoadingState, copyLink: true });
                                                const { base64_600 } = await getSocialCardsBase64();
                                                const fetchBody = {
                                                    surveyData: surveyResponse,
                                                    surveyId: surveyResponse.id,
                                                    // base64_300,
                                                    base64_600,
                                                }
                                                await fetch("/.netlify/functions/storeSurveyLink", { method: "POST", body: JSON.stringify(fetchBody) });
                                                if (analytics) {
                                                    analytics.track({
                                                        event: 'Generated sandbox link',
                                                        userId: currentUserId,
                                                        properties: {
                                                            surveyId: surveyResponse.id,
                                                        }
                                                    });
                                                }
                                                const copyLink = getCopyLink(surveyResponse, sandboxURL);
                                                clipboard.copy(copyLink);
                                                showNotification({
                                                    title: 'Link copied',
                                                    color: "teal",
                                                    style: { top: '60px' }
                                                });
                                                setIconLoadingState({ ...iconLoadingState, copyLink: false });
                                            }}
                                            size={32}
                                        >
                                            <Loader size={20} style={{ display: iconLoadingState.copyLink ? 'block' : 'none' }} className={classes.toolbarIcon} />
                                            <IconLink
                                                style={{ display: iconLoadingState.copyLink ? 'none' : 'block' }}
                                                stroke={1.5}
                                                size={20}
                                                color='black'
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Menu className={classes.hideForMobile}>
                                        <Menu.Target>
                                            <Tooltip
                                                label="Save results"
                                                color="dark"
                                            >
                                                <ActionIcon
                                                    variant='transparent'
                                                    size={32}
                                                    sx={{
                                                        '&[data-disabled]': {
                                                            background: 'none',
                                                            border: 'none'
                                                        }
                                                    }}
                                                >
                                                    {
                                                        preparingData ?
                                                            <Loader size={20} className={classes.toolbarIcon} />
                                                            :
                                                            <IconDownload
                                                                stroke={1.5}
                                                                size={20}
                                                                color='black'
                                                            />
                                                    }
                                                </ActionIcon>
                                            </Tooltip>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                icon={<IconCopy stroke={1.5} size={20} />}
                                                onClick={async () => {
                                                    setPreparingData(true);
                                                    await copyPNG();
                                                    setPreparingData(false);
                                                    if (analytics) {
                                                        analytics.track({
                                                            event: 'Downloaded sandbox data',
                                                            userId: currentUserId,
                                                            properties: {
                                                                surveyId: surveyResponse.id,
                                                                format: 'png'
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                Copy graph
                                            </Menu.Item>
                                            <Menu.Item
                                                icon={<IconChartBar stroke={1.5} size={20} />}
                                                onClick={async () => {
                                                    setPreparingData(true);
                                                    await downloadPNG();
                                                    setPreparingData(false);
                                                    if (analytics) {
                                                        analytics.track({
                                                            event: 'Downloaded sandbox data',
                                                            userId: currentUserId,
                                                            properties: {
                                                                surveyId: surveyResponse.id,
                                                                format: 'png'
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                Download graph
                                            </Menu.Item>
                                            <Menu.Item
                                                icon={<IconCodeDots stroke={1.5} size={20} />}
                                                onClick={() => {
                                                    downloadJSON([surveyResponse]);
                                                    if (analytics) {
                                                        analytics.track({
                                                            event: 'Downloaded sandbox data',
                                                            userId: currentUserId,
                                                            properties: {
                                                                surveyId: surveyResponse.id,
                                                                format: 'json'
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                Download JSON
                                            </Menu.Item>
                                            <Menu.Item
                                                icon={<IconTable stroke={1.5} size={20} />}
                                                onClick={() => {
                                                    downloadCSV(surveyResponse.surveyData);
                                                    if (analytics) {
                                                        analytics.track({
                                                            event: 'Downloaded sandbox data',
                                                            userId: currentUserId,
                                                            properties: {
                                                                surveyId: surveyResponse.id,
                                                                format: 'csv'
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                Download CSV
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </>
                            }
                            <Select
                                // size={isSmall ? 'sm' : 'md'}
                                size='sm'
                                // size= 'sm'
                                radius='md'
                                maxDropdownHeight={300}
                                className={classes.examplesSelect}
                                placeholder={surveysStillLoading ? '' : 'Load an example'}
                                icon={surveysStillLoading && <Loader className={classes.toolbarIcon} size={20} />}
                                data={urlExamples}
                                value={exampleSearchValue}
                                disabled={isRunningNewSurvey || surveysStillLoading}
                                rightSection={
                                    surveysStillLoading ?
                                        <Loader className={classes.toolbarIcon} size={20} />
                                        :
                                        exampleSearchValue !== '' && (
                                            <ActionIcon
                                                size={32}
                                                radius="xl"
                                                onMouseDown={async () => {
                                                    setExampleSearchValue('');
                                                    setSurveyResponse({});
                                                    // if (embeddingsShown) await toggleEmbeddings();
                                                }}
                                            >
                                                <IconX stroke={1.5} size={20} />
                                            </ActionIcon>
                                        )
                                }
                                onChange={(surveyId) => {
                                    const chosenSurvey = exampleSurveys.filter(survey => survey.id === surveyId);
                                    if (chosenSurvey.length !== 0) {
                                        setSurveyResponse(chosenSurvey[0]);
                                    }
                                }}
                            />
                            <Tooltip
                                label={`Upload questions`}
                                color="dark"
                                className={classes.hideForMobile}
                                position='bottom-start'
                            >
                                <ActionIcon
                                    variant='transparent'
                                    size={32}
                                    disabled={currentUser == undefined}
                                    onMouseDown={() => {
                                        setFileModalOpened(true);
                                    }}
                                >
                                    <IconCloudUpload
                                        stroke={1.5}
                                        size={20}
                                        color='black'
                                        className={currentUser ? undefined : classes.toolbarItemDisabled}
                                    />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </div>
                </div>
                <SurveyHistory
                    surveyResponse={surveyResponse}
                    userSurveyHistory={userSurveyHistory}
                    setSurveyResponse={setSurveyResponse}
                    historyShown={historyShown}
                    setHistoryShown={setHistoryShown}
                    currentUser={currentUser}
                    loadingText={historyLoadingText}
                    setExampleSearchValue={setExampleSearchValue}
                    userSurveyHistoryLoaded={userSurveyHistoryLoaded}
                />
                <SurveyPlot
                    surveyResponse={surveyResponse}
                    historyShown={historyShown}
                    isEmbeddingPlot={embeddingsShown}
                    embeddingData={embeddings2d}
                    selectedEmbeddingIndex={selectedEmbeddingIndex}
                    shownQuestionIndices={shownQuestionIndices}
                    similarQuestionData={similarQuestionData}
                    similarDataPlotPrimary={similarDataPlotPrimary}
                    setSimilarDataPlotPrimary={setSimilarDataPlotPrimary}
                    closestTextsAndOptions={embeddingText}
                    isFittingTSNE={embeddingsState < 3}
                    isSmall={isSmall}
                    isMedium={isMedium}
                    hasMouse={hasMouse}
                    ref={plotRef}
                />
                {
                    embeddingsShown ?
                        <ConfidenceForm
                            embeddingsState={embeddingsState}
                            step={step}
                            numTSNEStepsBeforePlot={numTSNEStepsBeforePlot}
                            similarQuestions={embeddingText}
                            selectedEmbeddingIndex={selectedEmbeddingIndex}
                            setSelectedEmbeddingIndex={setSelectedEmbeddingIndex}
                            surveyQuestion={surveyResponse?.surveyQuestion}
                            model={surveyResponse?.surveyModel}
                            shownQuestionIndices={shownQuestionIndices}
                            setShownQuestionIndices={setShownQuestionIndices}
                            embeddingScores={embeddingScores}
                            isLoading={embeddingsState < 3}
                            embeddingsError={embeddingsError}
                            restartEmbeddings={retrieveDataAndPlotEmbeddings}
                            similarQuestionData={similarQuestionData}
                            similarDataPlotPrimary={similarDataPlotPrimary}
                            setSimilarDataPlotPrimary={setSimilarDataPlotPrimary}
                        />
                        :
                        <SurveyForm
                            surveyResponse={surveyResponse}
                            setSurveyResponse={setSurveyResponse}
                            setHistoryShown={setHistoryShown}
                            userSurveyHistory={userSurveyHistory}
                            setUserSurveyHistory={setUserSurveyHistory}
                            isRunningNewSurvey={isRunningNewSurvey}
                            setIsRunningNewSurvey={setIsRunningNewSurvey}
                            setConfidence={setConfidence}
                            currentUser={currentUser}
                            setUserError={setUserError}
                            numCreditsAvailable={numCreditsAvailable}
                            updateCreditUsage={updateCreditUsage}
                            customModels={customModels}
                        />
                }
            </div>
            {
                <>
                    <SocialCard
                        surveyResponse={surveyResponse}
                        isSmall={false}
                        isMedium={true}
                        width={840}
                        height={441}
                        margins={{ top: 30, right: surveyResponse.isPanel ? 55 : 30, bottom: 42, left: surveyResponse.isPanel ? 55 : 60 }}
                        ref = {socialCardRef6001230}
                        hideAxisLabels = {true}
                    />
                </>
            }
        </>
    )
}

export default Sandbox;

// Download data option for both